import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Button,
  FORM_CONTROL_CLASS,
  FORM_LABEL_CLASS,
  FORM_TEXTAREA_CLASS,
  Modal,
  SUB_CONFIRM_LAYER,
  useConfirmDialog,
} from '@safetyhub/ui';
import {
  LEGAL_OBLIGATION_CONDITION_AXIS_LABEL,
  LEGAL_OBLIGATION_CONDITION_SOURCE_KIND_LABEL,
  LEGAL_OBLIGATION_INTERVAL_ANCHOR_LABEL,
  LEGAL_OBLIGATION_INTERVAL_UNIT_LABEL,
  formatAnnexLabel,
  formatDateISO,
  formatObligationInterval,
  type AdminLegalObligationConditionView,
  type AdminLegalObligationIntervalVariantView,
  type AdminLegalObligationRow,
  type LegalObligationConditionAxisValue,
  type LegalObligationConditionSourceKindValue,
  type ObligationIntervalVariantSelector,
  type ObligationIntervalVariantTarget,
} from '@safetyhub/shared';
import { adminApi, extractApiError } from '../../lib/api';
import { legalObligationCatalogApi } from '../../lib/legalObligationCatalog';
import {
  buildConditionCreateBody,
  buildConditionUpdateBody,
  buildVariantCreateBody,
  buildVariantUpdateBody,
  conditionAxisUnit,
  conditionFormBlockedReason,
  conditionToForm,
  emptyConditionForm,
  emptyVariantForm,
  isNumericAxis,
  variantFormBlockedReason,
  variantToForm,
  type ConditionFormState,
  type VariantFormState,
} from '../../lib/legalObligationConditionFields';
import { useToastStore } from '../../stores/toast';
import LawAnnexesModal from '../regulations/LawAnnexesModal';
import AdminRuleAnnexPickerModal from './AdminRuleAnnexPickerModal';

interface Props {
  /** 목록의 «최신» 행 — 부모가 목록을 다시 읽으면 이 prop도 새 값이 온다(쓰기마다 version이 오른다) */
  row: AdminLegalObligationRow;
  onClose: () => void;
  /** 쓰기 하나가 끝날 때마다 — 부모가 목록·타임라인을 다시 읽는다 */
  onChanged: () => void;
}

/**
 * **적용 조건 · 간격 변형 줄 편집** — 카탈로그 쓰기 경로 하나 설계의 W3(2026-11-04 · 서버 입구는 W2).
 *
 * ## 의무 편집과 다른 모달인 이유 — 기록 단위가 다르다
 * 의무 편집 모달은 칸을 모아 **한 번 저장 = 개정 1건**이다. 줄 편집은 **줄 하나의 저장마다** 서버가 따로 기록한다(서버 카탈로그 서비스
 * docblock): 조건·변형의 규칙 값 = **개정**(이력 1행 · version +1) · 조건의 근거 · 「대조 완료」 = **별 기록**(version 불변). 한 모달에 섞으면
 * 「저장」 하나가 몇 건의 개정인지 운영자가 읽을 수 없다. 그래서 줄마다 저장 버튼을 두고 그 결과(개정 여부)를 토스트로 알린다.
 *
 * ## 판정은 서버가 한다
 * 축 형상 · 근거 형상 · 근거 «실재»(현행 버전에 그 별표가 있나) · 변형 형상 · 변형 삭제 참조는 서버 `legal-obligation-catalog-shape.ts`가 400 +
 * 사유로 준다 — 이 화면은 그 문구를 그대로 보인다. 폼이 먼저 막는 것은 폼만 보고 닫히는 것뿐이다(`legalObligationConditionFields.ts`).
 */
export default function CatalogConditionsModal({ row, onClose, onChanged }: Props) {
  const { showToast } = useToastStore();
  const [confirm, confirmDialog] = useConfirmDialog();
  const [conditionEdit, setConditionEdit] = useState<{ current: AdminLegalObligationConditionView | null; form: ConditionFormState } | null>(null);
  const [variantEdit, setVariantEdit] = useState<{ current: AdminLegalObligationIntervalVariantView | null; form: VariantFormState } | null>(null);
  const usedAxes = new Set(row.conditions.map((c) => c.axis));
  const freeAxes = (Object.keys(LEGAL_OBLIGATION_CONDITION_AXIS_LABEL) as LegalObligationConditionAxisValue[]).filter((a) => !usedAxes.has(a));
  const [newAxis, setNewAxis] = useState<LegalObligationConditionAxisValue | ''>('');
  const [newTarget, setNewTarget] = useState<ObligationIntervalVariantTarget>('PERIOD');

  const fail = (error: unknown) => showToast(extractApiError(error).message, 'error');

  const conditionSave = useMutation({
    mutationFn: async (edit: { current: AdminLegalObligationConditionView | null; form: ConditionFormState }) => {
      if (edit.current === null) return legalObligationCatalogApi.createCondition(row.id, buildConditionCreateBody(edit.form));
      const body = buildConditionUpdateBody(edit.current, edit.form);
      if (body === null) throw new Error('바뀐 칸이 없습니다.');
      return legalObligationCatalogApi.updateCondition(row.id, edit.current.id, body);
    },
    onSuccess: (result) => {
      showToast(writeResultMessage(result.revised, result.sourceChanged, result.version), 'success');
      setConditionEdit(null);
      onChanged();
    },
    onError: fail,
  });

  const variantSave = useMutation({
    mutationFn: async (edit: { current: AdminLegalObligationIntervalVariantView | null; form: VariantFormState }) => {
      if (edit.current === null) return legalObligationCatalogApi.createVariant(row.id, buildVariantCreateBody(edit.form));
      const body = buildVariantUpdateBody(edit.current, edit.form);
      if (body === null) throw new Error('바뀐 칸이 없습니다.');
      return legalObligationCatalogApi.updateVariant(row.id, edit.current.id, body);
    },
    onSuccess: (result) => {
      showToast(writeResultMessage(result.revised, false, result.version), 'success');
      setVariantEdit(null);
      onChanged();
    },
    onError: fail,
  });

  const action = useMutation({
    mutationFn: (run: () => Promise<unknown>) => run(),
    onSuccess: () => onChanged(),
    onError: fail,
  });

  const removeCondition = async (c: AdminLegalObligationConditionView) => {
    const ok = await confirm({
      title: '조건 줄 삭제',
      body: `「${LEGAL_OBLIGATION_CONDITION_AXIS_LABEL[c.axis]}」 조건을 지웁니다. 개정 1건이 남고 버전이 오릅니다.`,
      confirmLabel: '삭제',
      danger: true,
      ...SUB_CONFIRM_LAYER,
    });
    if (ok) action.mutate(() => legalObligationCatalogApi.deleteCondition(row.id, c.id).then((r) => showToast(writeResultMessage(r.revised, false, r.version), 'success')));
  };

  const removeVariant = async (v: AdminLegalObligationIntervalVariantView) => {
    const ok = await confirm({
      title: '간격 변형 줄 삭제',
      body: '이 변형을 지웁니다. 개정 1건이 남고 버전이 오릅니다. 다른 의무가 사건 공유 조건으로 들었거나 현장 기준일 입력이 골랐으면 지울 수 없습니다.',
      confirmLabel: '삭제',
      danger: true,
      ...SUB_CONFIRM_LAYER,
    });
    if (ok) action.mutate(() => legalObligationCatalogApi.deleteVariant(row.id, v.id).then((r) => showToast(writeResultMessage(r.revised, false, r.version), 'success')));
  };

  const busy = conditionSave.isPending || variantSave.isPending || action.isPending;

  return (
    <Modal
      open
      onClose={onClose}
      title={`적용 조건 · 간격 변형 — ${row.title}`}
      size="xl"
      closeLocked={busy}
      footer={
        <Button variant="secondary" size="sm" onClick={onClose} disabled={busy}>
          닫기
        </Button>
      }
    >
      <div className="space-y-6">
        <p data-testid="lines-revision-notice" className="rounded-card border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          줄마다 저장하는 즉시 기록됩니다 — 조건·변형의 값을 바꾸면 <strong>개정</strong>(이력 1건 · 버전 +1), 조건의 근거만 바꾸거나
          「대조 완료」를 누르면 버전을 올리지 않는 <strong>별 기록</strong>이 남습니다(이력 화면에 함께 보입니다). 현재 v{row.version}.
        </p>

        {/* ── 적용 조건 ── */}
        <section data-testid="conditions-editor">
          <div className={FORM_LABEL_CLASS}>적용 조건</div>
          {row.conditions.length === 0 ? (
            <p className="text-sm text-ink-500">
              등록된 조건이 없습니다. 조건이 없다고 「전 사업장 적용」이라는 뜻은 아닙니다 — 적용 범위 칸을 보세요.
            </p>
          ) : (
            <ul className="divide-y divide-line rounded-card border border-line">
              {row.conditions.map((c) => (
                <li key={c.id} data-testid={`condition-${c.axis}`} className="flex flex-wrap items-start justify-between gap-2 p-3 text-sm">
                  <div>
                    <div className="text-ink-900">
                      {LEGAL_OBLIGATION_CONDITION_AXIS_LABEL[c.axis]}: {describeCondition(c)}
                      {c.variesByIndustry && <span className="text-ink-500"> (업종에 따라 기준이 다름 — 미달이어도 「확인 필요」)</span>}
                    </div>
                    <div className="text-xs text-ink-500" data-testid="condition-annex-source">
                      {describeConditionSource(c)}
                    </div>
                  </div>
                  <div className="inline-flex gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={busy}
                      onClick={() => action.mutate(() => legalObligationCatalogApi.verifyCondition(row.id, c.id).then(() => showToast('별표와 대조 완료로 기록했습니다(개정 아님).', 'success')))}
                    >
                      대조 완료
                    </Button>
                    <Button variant="secondary" size="sm" disabled={busy} onClick={() => setConditionEdit({ current: c, form: conditionToForm(c) })}>
                      편집
                    </Button>
                    <Button variant="secondary" size="sm" disabled={busy} onClick={() => void removeCondition(c)}>
                      삭제
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {conditionEdit === null && freeAxes.length > 0 && (
            <div className="mt-2 flex items-center gap-2">
              <select
                aria-label="추가할 조건 축"
                value={newAxis}
                onChange={(e) => setNewAxis(e.target.value as LegalObligationConditionAxisValue | '')}
                className={`${FORM_CONTROL_CLASS} w-64`}
              >
                <option value="">축 고르기</option>
                {freeAxes.map((a) => (
                  <option key={a} value={a}>
                    {LEGAL_OBLIGATION_CONDITION_AXIS_LABEL[a]}
                  </option>
                ))}
              </select>
              <Button
                variant="secondary"
                size="sm"
                disabled={newAxis === '' || busy}
                onClick={() => newAxis !== '' && setConditionEdit({ current: null, form: emptyConditionForm(newAxis) })}
              >
                조건 줄 추가
              </Button>
            </div>
          )}
          {conditionEdit && (
            <ConditionEditor
              key={conditionEdit.current?.id ?? `new-${conditionEdit.form.axis}`}
              edit={conditionEdit}
              saving={conditionSave.isPending}
              onChange={(form) => setConditionEdit({ ...conditionEdit, form })}
              onCancel={() => setConditionEdit(null)}
              onSave={() => conditionSave.mutate(conditionEdit)}
            />
          )}
        </section>

        {/* ── 간격 변형 ── */}
        <section data-testid="variants-editor">
          <div className={FORM_LABEL_CLASS}>간격 변형</div>
          {row.intervalVariants.length === 0 ? (
            <p className="text-sm text-ink-500">등록된 변형이 없습니다 — 모든 현장이 주기 규칙의 간격을 그대로 씁니다.</p>
          ) : (
            <ul className="divide-y divide-line rounded-card border border-line">
              {row.intervalVariants.map((v) => (
                <li key={v.id} data-testid={`variant-${v.id}`} className="flex flex-wrap items-start justify-between gap-2 p-3 text-sm">
                  <div className="text-ink-900">{describeVariant(v)}</div>
                  <div className="inline-flex gap-2">
                    <Button variant="secondary" size="sm" disabled={busy} onClick={() => setVariantEdit({ current: v, form: variantToForm(v) })}>
                      편집
                    </Button>
                    <Button variant="secondary" size="sm" disabled={busy} onClick={() => void removeVariant(v)}>
                      삭제
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-1 text-xs text-ink-500">현장 업종이 없거나 변형보다 얕으면 가장 짧은 간격을 적용하고 「확인 필요」로 표시합니다.</p>
          {variantEdit === null && (
            <div className="mt-2 flex items-center gap-2">
              <select
                aria-label="추가할 변형 대상"
                value={newTarget}
                onChange={(e) => setNewTarget(e.target.value as ObligationIntervalVariantTarget)}
                className={`${FORM_CONTROL_CLASS} w-64`}
              >
                {(Object.keys(VARIANT_TARGET_LABEL) as ObligationIntervalVariantTarget[]).map((t) => (
                  <option key={t} value={t}>
                    {VARIANT_TARGET_LABEL[t]}
                  </option>
                ))}
              </select>
              <Button variant="secondary" size="sm" disabled={busy} onClick={() => setVariantEdit({ current: null, form: emptyVariantForm(newTarget) })}>
                변형 줄 추가
              </Button>
            </div>
          )}
          {variantEdit && (
            <VariantEditor
              key={variantEdit.current?.id ?? `new-${variantEdit.form.target}`}
              edit={variantEdit}
              saving={variantSave.isPending}
              onChange={(form) => setVariantEdit({ ...variantEdit, form })}
              onCancel={() => setVariantEdit(null)}
              onSave={() => variantSave.mutate(variantEdit)}
            />
          )}
        </section>
      </div>
      {confirmDialog}
    </Modal>
  );
}

/** 쓰기 결과 문구 — 어느 축이 움직였나(서버 응답 `revised`·`sourceChanged`) */
export function writeResultMessage(revised: boolean, sourceChanged: boolean, version: number): string {
  if (revised && sourceChanged) return `개정했습니다(v${version}) — 근거 변경도 별 기록으로 남았습니다.`;
  if (revised) return `개정했습니다 — 이력 1건이 남고 v${version}이 되었습니다.`;
  if (sourceChanged) return '근거를 바꿨습니다 — 별 기록이 남고 버전은 그대로입니다.';
  return '바뀐 값이 없어 아무것도 기록하지 않았습니다.';
}

const VARIANT_TARGET_LABEL: Record<ObligationIntervalVariantTarget, string> = {
  PERIOD: '반복 간격',
  INITIAL: '최초 간격',
  EVENT_DEADLINE: '사건 기한',
};

const VARIANT_SELECTOR_LABEL: Record<ObligationIntervalVariantSelector, string> = {
  SITE_FACT: '현장 사실(축 값)',
  EVENT_CHOICE: '사건 등록 시 선택',
  RESULT_FACT: '측정·진단 결과',
  // 2026-11-04 — 대상 사람의 사실(값 `<법정 직위>:<사실>` · 예 ART_18:PHYSICIAN = 보건관리자가 의사). 사람 대상 의무에서만 읽힌다
  PERSON_FACT: '대상자 사실(직위:사실)',
};

function describeCondition(c: AdminLegalObligationConditionView): string {
  if (c.valueKeys.length > 0) return c.valueKeys.join(', ');
  const unit = c.unit ?? '';
  const parts: string[] = [];
  if (c.minValue !== null) parts.push(`${c.minValue}${unit} ${c.minInclusive ? '이상' : '초과'}`);
  if (c.maxValue !== null) parts.push(`${c.maxValue}${unit} ${c.maxInclusive ? '이하' : '미만'}`);
  return parts.length > 0 ? parts.join(' · ') : '값 미정';
}

/**
 * 근거 한 줄 — 점검 큐 「별표 미확인」이 근거 키와 조건 단위 `lastVerifiedAt`으로 판정한다. 「근거 미지정」은 숨기지 않고 적는다
 * (별표가 바뀌어도 점검 큐가 그 조건을 부르지 못한다 — 「바뀐 적 없다」가 아니다).
 */
export function describeConditionSource(c: AdminLegalObligationConditionView): string {
  const verified = c.lastVerifiedAt ? `마지막 대조 ${formatDateISO(new Date(c.lastVerifiedAt))}` : '대조 기록 없음';
  if (c.sourceKind === 'ARTICLE') return `근거: 조문(별표 없음) · ${verified}`;
  if (c.sourceKind === 'UNSPECIFIED' || !c.sourceAnnexNumber) {
    return `근거 미지정 — 별표가 바뀌어도 점검 큐가 이 조건을 부르지 못합니다 · ${verified}`;
  }
  const doc = c.sourceKind === 'LAW_ANNEX' ? `법령ID ${c.sourceLawId}` : `행정규칙ID ${c.sourceAdminRuleId}`;
  return `근거 ${c.sourceKind === 'LAW_ANNEX' ? '법령' : '고시'} 별표: ${doc} ${formatAnnexLabel('별표', c.sourceAnnexNumber, c.sourceAnnexSubNumber)} · ${verified}`;
}

function describeVariant(v: AdminLegalObligationIntervalVariantView): string {
  const anchor = v.anchor === 'ROLLING' ? '(실시일부터)' : v.anchor === 'CALENDAR' ? '(달력)' : '';
  const who =
    v.selector === 'SITE_FACT'
      ? ` — 현장 ${v.axis ? LEGAL_OBLIGATION_CONDITION_AXIS_LABEL[v.axis] : ''} ${v.valueKeys.join(', ')}`
      : v.selector === 'RESULT_FACT'
        ? ` — 측정·진단 결과 ${v.valueKeys.join(', ')}${v.oneCycleOnly ? ' (다음 1회 한정)' : ''}`
        : v.selector === 'PERSON_FACT'
          ? ` — 대상자 사실 ${v.valueKeys.join(', ')}${v.label ? ` (${v.label})` : ''}`
          : ` — 등록 시 선택: ${v.label ?? ''}`;
  return `${VARIANT_TARGET_LABEL[v.target]} → ${formatObligationInterval(v.interval)}${anchor}${who}`;
}

// ── 조건 편집기 ──────────────────────────────────────────────────────────

function ConditionEditor({
  edit,
  saving,
  onChange,
  onCancel,
  onSave,
}: {
  edit: { current: AdminLegalObligationConditionView | null; form: ConditionFormState };
  saving: boolean;
  onChange: (form: ConditionFormState) => void;
  onCancel: () => void;
  onSave: () => void;
}) {
  const { form, current } = edit;
  const [picker, setPicker] = useState<'LAW' | 'ADMIN_RULE' | null>(null);
  const set = <K extends keyof ConditionFormState>(key: K, value: ConditionFormState[K]) => onChange({ ...form, [key]: value });
  const numeric = isNumericAxis(form.axis);
  const unit = conditionAxisUnit(form.axis);
  const lawNames = useQuery({
    queryKey: ['admin', 'regulations', 'types'],
    queryFn: async () => (await adminApi.getRegulationTypes()).data ?? [],
    enabled: picker === 'LAW',
  });
  const unchanged = current !== null && buildConditionUpdateBody(current, form) === null;
  const blockedReason = conditionFormBlockedReason(form) ?? (unchanged ? '바뀐 칸이 없습니다.' : undefined);
  const annexChosen = form.sourceAnnexNumber !== '' ? formatAnnexLabel('별표', form.sourceAnnexNumber.padStart(4, '0'), form.sourceAnnexSubNumber) : null;

  return (
    <div data-testid="condition-form" className="mt-3 space-y-3 rounded-card border border-line p-3">
      <div className="text-sm font-semibold text-ink-900">
        {current ? '조건 줄 편집' : '조건 줄 추가'} — {LEGAL_OBLIGATION_CONDITION_AXIS_LABEL[form.axis]}
      </div>
      {numeric ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {/* 수 입력과 포함 체크박스를 한 <label>에 넣지 않는다 — label 하나가 두 컨트롤을 감싸면 접근 이름이 섞인다 */}
          <div>
            <label className="block">
              <span className={FORM_LABEL_CLASS}>하한 ({unit})</span>
              <input type="text" inputMode="decimal" value={form.minValue} onChange={(e) => set('minValue', e.target.value)} className={FORM_CONTROL_CLASS} />
            </label>
            <label className="mt-1 inline-flex items-center gap-1 text-xs text-ink-600">
              <input type="checkbox" checked={form.minInclusive} onChange={(e) => set('minInclusive', e.target.checked)} />
              하한 포함(「이상」)
            </label>
          </div>
          <div>
            <label className="block">
              <span className={FORM_LABEL_CLASS}>상한 ({unit})</span>
              <input type="text" inputMode="decimal" value={form.maxValue} onChange={(e) => set('maxValue', e.target.value)} className={FORM_CONTROL_CLASS} />
            </label>
            <label className="mt-1 inline-flex items-center gap-1 text-xs text-ink-600">
              <input type="checkbox" checked={form.maxInclusive} onChange={(e) => set('maxInclusive', e.target.checked)} />
              상한 포함(「이하」)
            </label>
          </div>
        </div>
      ) : (
        <label className="block">
          <span className={FORM_LABEL_CLASS}>값 집합(쉼표로 구분)</span>
          <input type="text" value={form.valueKeysText} onChange={(e) => set('valueKeysText', e.target.value)} className={FORM_CONTROL_CLASS} />
          <span className="mt-1 block text-xs text-ink-500">축의 값 키 그대로 적습니다(업종 = KSIC 코드 접두 · 특별교육 작업 = 호 번호 · 기술지도 구분 = CONSTRUCTION·ELECTRICAL_TELECOM). 표에 없는 값은 저장할 때 서버가 거부합니다. 비우면 그 축의 모든 값입니다.</span>
        </label>
      )}
      <label className="inline-flex items-center gap-2 text-sm text-ink-700">
        <input type="checkbox" checked={form.variesByIndustry} onChange={(e) => set('variesByIndustry', e.target.checked)} />
        업종에 따라 기준이 다른 «일반형» 값입니다(조문이 문턱을 고정한 줄엔 켜지 마세요)
      </label>

      <div>
        <label className="block">
          <span className={FORM_LABEL_CLASS}>근거 종류</span>
          <select
            value={form.sourceKind}
            onChange={(e) => onChange({ ...form, sourceKind: e.target.value as LegalObligationConditionSourceKindValue, sourceLawId: '', sourceAdminRuleId: '', sourceAnnexNumber: '', sourceAnnexSubNumber: '' })}
            className={FORM_CONTROL_CLASS}
          >
            {(Object.keys(LEGAL_OBLIGATION_CONDITION_SOURCE_KIND_LABEL) as LegalObligationConditionSourceKindValue[]).map((k) => (
              <option key={k} value={k}>
                {LEGAL_OBLIGATION_CONDITION_SOURCE_KIND_LABEL[k]}
              </option>
            ))}
          </select>
        </label>
        {(form.sourceKind === 'LAW_ANNEX' || form.sourceKind === 'ADMIN_RULE_ANNEX') && (
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
            <span data-testid="condition-source-chosen" className="text-ink-700">
              {annexChosen
                ? `${form.sourceKind === 'LAW_ANNEX' ? `법령ID ${form.sourceLawId}` : `행정규칙ID ${form.sourceAdminRuleId}`} ${annexChosen}`
                : '고른 별표 없음'}
            </span>
            <Button variant="secondary" size="sm" onClick={() => setPicker(form.sourceKind === 'LAW_ANNEX' ? 'LAW' : 'ADMIN_RULE')}>
              별표 고르기
            </Button>
          </div>
        )}
        <span className="mt-1 block text-xs text-ink-500">
          별표를 근거로 두면 그 별표가 개정될 때 점검 큐가 이 조건을 부릅니다. 「근거 미지정」은 부르지 못합니다.
        </span>
      </div>

      <label className="block">
        <span className={FORM_LABEL_CLASS}>출처 메모</span>
        <textarea value={form.sourceNote} maxLength={5000} onChange={(e) => set('sourceNote', e.target.value)} className={FORM_TEXTAREA_CLASS} />
      </label>
      <label className="block">
        <span className={FORM_LABEL_CLASS}>개정 사유</span>
        <textarea value={form.changeReason} maxLength={2000} onChange={(e) => set('changeReason', e.target.value)} className={FORM_TEXTAREA_CLASS} />
        <span className="mt-1 block text-xs text-ink-500">값이 바뀌어 개정이 일어날 때 이력에 함께 남습니다.</span>
      </label>
      <div className="flex justify-end gap-2">
        <Button variant="secondary" size="sm" onClick={onCancel} disabled={saving}>
          취소
        </Button>
        <Button variant="primary" size="sm" onClick={onSave} disabled={saving || blockedReason !== undefined} title={blockedReason}>
          {saving ? '저장 중...' : current ? '조건 저장' : '조건 추가'}
        </Button>
      </div>

      {picker === 'LAW' && (
        <LawAnnexesModal
          open
          onClose={() => setPicker(null)}
          lawNames={lawNames.data ?? []}
          overlayZClassName={SUB_CONFIRM_LAYER.overlayZClassName}
          overlayBgClassName={SUB_CONFIRM_LAYER.overlayBgClassName}
          onPick={(p) => {
            onChange({ ...form, sourceLawId: p.lawId, sourceAdminRuleId: '', sourceAnnexNumber: p.annexNumber, sourceAnnexSubNumber: p.annexSubNumber });
            setPicker(null);
          }}
        />
      )}
      {picker === 'ADMIN_RULE' && (
        <AdminRuleAnnexPickerModal
          onClose={() => setPicker(null)}
          overlayZClassName={SUB_CONFIRM_LAYER.overlayZClassName}
          overlayBgClassName={SUB_CONFIRM_LAYER.overlayBgClassName}
          onPick={(p) => {
            onChange({ ...form, sourceAdminRuleId: p.ruleId, sourceLawId: '', sourceAnnexNumber: p.annexNumber, sourceAnnexSubNumber: p.annexSubNumber });
            setPicker(null);
          }}
        />
      )}
    </div>
  );
}

// ── 변형 편집기 ──────────────────────────────────────────────────────────

function VariantEditor({
  edit,
  saving,
  onChange,
  onCancel,
  onSave,
}: {
  edit: { current: AdminLegalObligationIntervalVariantView | null; form: VariantFormState };
  saving: boolean;
  onChange: (form: VariantFormState) => void;
  onCancel: () => void;
  onSave: () => void;
}) {
  const { form, current } = edit;
  const set = <K extends keyof VariantFormState>(key: K, value: VariantFormState[K]) => onChange({ ...form, [key]: value });
  const unchanged = current !== null && buildVariantUpdateBody(current, form) === null;
  const blockedReason = variantFormBlockedReason(form) ?? (unchanged ? '바뀐 칸이 없습니다.' : undefined);

  return (
    <div data-testid="variant-form" className="mt-3 space-y-3 rounded-card border border-line p-3">
      <div className="text-sm font-semibold text-ink-900">{current ? '변형 줄 편집' : '변형 줄 추가'}</div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block">
          <span className={FORM_LABEL_CLASS}>대체할 간격</span>
          <select value={form.target} onChange={(e) => set('target', e.target.value as ObligationIntervalVariantTarget)} className={FORM_CONTROL_CLASS}>
            {(Object.keys(VARIANT_TARGET_LABEL) as ObligationIntervalVariantTarget[]).map((t) => (
              <option key={t} value={t}>
                {VARIANT_TARGET_LABEL[t]}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className={FORM_LABEL_CLASS}>고르는 방식</span>
          <select value={form.selector} onChange={(e) => set('selector', e.target.value as ObligationIntervalVariantSelector)} className={FORM_CONTROL_CLASS}>
            {(Object.keys(VARIANT_SELECTOR_LABEL) as ObligationIntervalVariantSelector[]).map((s) => (
              <option key={s} value={s}>
                {VARIANT_SELECTOR_LABEL[s]}
              </option>
            ))}
          </select>
        </label>
        {form.selector === 'SITE_FACT' && (
          <label className="block">
            <span className={FORM_LABEL_CLASS}>현장 사실 축</span>
            <select value={form.axis} onChange={(e) => set('axis', e.target.value as LegalObligationConditionAxisValue | '')} className={FORM_CONTROL_CLASS}>
              <option value="">—</option>
              {(Object.keys(LEGAL_OBLIGATION_CONDITION_AXIS_LABEL) as LegalObligationConditionAxisValue[]).map((a) => (
                <option key={a} value={a}>
                  {LEGAL_OBLIGATION_CONDITION_AXIS_LABEL[a]}
                </option>
              ))}
            </select>
          </label>
        )}
        {form.selector !== 'EVENT_CHOICE' && (
          <label className="block">
            <span className={FORM_LABEL_CLASS}>값 집합(쉼표로 구분)</span>
            <input type="text" value={form.valueKeysText} onChange={(e) => set('valueKeysText', e.target.value)} className={FORM_CONTROL_CLASS} />
          </label>
        )}
        <label className="block">
          <span className={FORM_LABEL_CLASS}>{form.selector === 'EVENT_CHOICE' ? '선택지 문구' : '설명(선택)'}</span>
          <input type="text" value={form.label} maxLength={300} onChange={(e) => set('label', e.target.value)} className={FORM_CONTROL_CLASS} />
        </label>
        <label className="block">
          <span className={FORM_LABEL_CLASS}>간격</span>
          <div className="flex gap-2">
            <input
              type="number"
              min={0}
              aria-label="변형 간격 수"
              value={form.intervalCount}
              onChange={(e) => set('intervalCount', e.target.value)}
              className={FORM_CONTROL_CLASS}
            />
            <select aria-label="변형 간격 단위" value={form.intervalUnit} onChange={(e) => set('intervalUnit', e.target.value as VariantFormState['intervalUnit'])} className={FORM_CONTROL_CLASS}>
              {(Object.keys(LEGAL_OBLIGATION_INTERVAL_UNIT_LABEL) as Array<VariantFormState['intervalUnit']>).map((u) => (
                <option key={u} value={u}>
                  {LEGAL_OBLIGATION_INTERVAL_UNIT_LABEL[u]}
                </option>
              ))}
            </select>
          </div>
        </label>
        {form.target === 'PERIOD' && (
          <label className="block">
            <span className={FORM_LABEL_CLASS}>기산</span>
            <select value={form.intervalAnchor} onChange={(e) => set('intervalAnchor', e.target.value as VariantFormState['intervalAnchor'])} className={FORM_CONTROL_CLASS}>
              <option value="">규칙 그대로</option>
              {(Object.keys(LEGAL_OBLIGATION_INTERVAL_ANCHOR_LABEL) as Array<Exclude<VariantFormState['intervalAnchor'], ''>>).map((a) => (
                <option key={a} value={a}>
                  {LEGAL_OBLIGATION_INTERVAL_ANCHOR_LABEL[a]}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      {form.selector === 'RESULT_FACT' && (
        <label className="inline-flex items-center gap-2 text-sm text-ink-700">
          <input type="checkbox" checked={form.oneCycleOnly} onChange={(e) => set('oneCycleOnly', e.target.checked)} />
          다음 주기 1회 한정
        </label>
      )}
      <label className="block">
        <span className={FORM_LABEL_CLASS}>출처 메모</span>
        <textarea value={form.sourceNote} maxLength={5000} onChange={(e) => set('sourceNote', e.target.value)} className={FORM_TEXTAREA_CLASS} />
      </label>
      <label className="block">
        <span className={FORM_LABEL_CLASS}>개정 사유</span>
        <textarea value={form.changeReason} maxLength={2000} onChange={(e) => set('changeReason', e.target.value)} className={FORM_TEXTAREA_CLASS} />
      </label>
      <p className="text-xs text-ink-500">대체할 간격은 이 의무의 주기 규칙에 있어야 합니다(반복·최초 간격 = 주기적 · 사건 기한 = 사유 발생 시).</p>
      <div className="flex justify-end gap-2">
        <Button variant="secondary" size="sm" onClick={onCancel} disabled={saving}>
          취소
        </Button>
        <Button variant="primary" size="sm" onClick={onSave} disabled={saving || blockedReason !== undefined} title={blockedReason}>
          {saving ? '저장 중...' : current ? '변형 저장' : '변형 추가'}
        </Button>
      </div>
    </div>
  );
}
